import type {SingleSelectOptionWithId} from '@github-ui/shared-single-select-components/types'
import {CalendarIcon, SingleSelectIcon, TypographyIcon} from '@primer/octicons-react'
import type {IssueField as IssueFieldPickerIssueField} from '@github-ui/item-picker/IssueFieldPicker'

export const KnownTypes = {
  text: {
    type: 'text' as const,
    graphqlType: 'TEXT' as const,
    displayName: 'Text',
    icon: TypographyIcon,
    valueField: 'textValue' as const,
  },
  single_select: {
    type: 'single_select' as const,
    graphqlType: 'SINGLE_SELECT' as const,
    displayName: 'Single Select',
    icon: SingleSelectIcon,
    valueField: 'singleSelectOptionId' as const,
  },
  date: {
    type: 'date' as const,
    graphqlType: 'DATE' as const,
    displayName: 'Date',
    icon: CalendarIcon,
    valueField: 'dateValue' as const,
  },
} as const

export type IssueFieldType = keyof typeof KnownTypes
export type GraphQLType = (typeof KnownTypes)[IssueFieldType]['graphqlType']
export type ValueFields = (typeof KnownTypes)[IssueFieldType]['valueField']

export const GraphQLTypes: Set<GraphQLType> = new Set(Object.values(KnownTypes).map(t => t.graphqlType))

export type KnownType = (typeof KnownTypes)[keyof typeof KnownTypes]

export type IssueFieldSingleSelectOption = {
  id: string
  name: string
  description: string | null | undefined
  color: 'BLUE' | 'GRAY' | 'GREEN' | 'ORANGE' | 'PINK' | 'PURPLE' | 'RED' | 'YELLOW'
}

type IssueFieldSelectionValueTypes = {
  TEXT: string
  SINGLE_SELECT: IssueFieldSingleSelectOption | null
  DATE: Date | null
}

export type IssueFieldSelectionValues = IssueFieldSelectionValueTypes[keyof IssueFieldSelectionValueTypes]

export type IssueField = {
  id: string
  name: string
  description: string
  priority?: number
  type: IssueFieldType
  options?: SingleSelectOptionWithId[]
}

export type IssueFieldSelection = {
  id: string
  name: string
  dataType: GraphQLType
}

export const AllKnownTypes = Object.values(KnownTypes)

export const GraphQLToIssueFieldType: Record<GraphQLType, IssueFieldType> = Object.entries(KnownTypes).reduce(
  (acc, [key, value]) => {
    acc[value.graphqlType] = key as IssueFieldType
    return acc
  },
  {} as Record<GraphQLType, IssueFieldType>,
)

export function fromGraphQLType(graphqlType: GraphQLType): IssueFieldType {
  return GraphQLToIssueFieldType[graphqlType]
}

export type FieldValues = Partial<{
  [K in IssueFieldType as (typeof KnownTypes)[K]['valueField']]: string
}>

export function getIssueFieldValue(fieldType: GraphQLType, value?: string): FieldValues {
  switch (fieldType) {
    case 'TEXT':
      return {textValue: value}
    case 'SINGLE_SELECT':
      return {singleSelectOptionId: value}
    case 'DATE':
      return {dateValue: (value && formatDateForStorage(new Date(value || ''))) || undefined}
  }
}

// Used to convert from the graphql type that IssueFieldPicker exposes to the shared type
export function asIssueFieldSelection(issueField: IssueFieldPickerIssueField | IssueFieldSelection) {
  return issueField as IssueFieldSelection
}

// TODO: Do this in one shared place
// Helper function to format dates consistently and avoid timezone issues
// The function sets the time to noon UTC (12:00 PM) to normalize the date.
// Noon UTC is chosen because it minimizes the risk of date shifts caused by timezone differences.
// For example, if a date is stored without a specific time, converting it across timezones could
// result in the date being shifted forward or backward. By fixing the time at noon UTC, we ensure
// that the date remains consistent regardless of the user's timezone.
// This function is intended for formatting dates before storing them in a database or API.
function formatDateForStorage(date: Date | null): string | null {
  if (!date) return null
  const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0))
  return normalizedDate.toISOString()
}
